/** @jsxImportSource @emotion/react */
import { css, useTheme } from "@emotion/react";
import React, { useEffect, useState, useRef } from "react";
import {
	Button,
	RadioGroup,
	RadioGroupItem,
	useToast,
} from "@coreai/component-library";
import ProjectTextField from "./ProjectTextField";
import TextFieldWithRadioDropdown from "./TextFieldWithRadioDropdown";
import FileInput from "./FileInput";
import {
	CampaignModel,
	patchAnalysis,
	executeAnalysis,
} from "../../../../services/analysisService";
import { ProjectsModel } from "../../../../services/projectsService";
import { RulesetExtItem, RulesetModel } from "../../../../services/rulesetService";
import CampaignTextField from "./CampaignTextField";
import NotesField from "./NotesField";
import { AssetModel, createAsset, triggerAssetDescription } from "../../../../services/assetsService";
import { useNavigate, useParams } from "react-router-dom";
import { useLanguage } from "LanguageContext";
import { getAnalysis } from "services/analysisService";
import Loader from "../../../MainComponents/Loader/Loader";
import NavigationBlocker from "components/MainComponents/NavigationBlocker/NavigationBlocker";
import YoutubeUrlUpload from "./YoutubeUrlUpload";
import PageLoader from "./../../../MainComponents/Loader/Loader"

declare global {
	interface Window {
	  isAnalysisDescFilled: boolean; 
	}
  }
 
export default function EditAnalysisForm() {
	const { t, language, setReRenderAnalysisList } = useLanguage();	
	const CHARACTER_LIMIT = 250000;
	const theme = useTheme();
	const [isUpload, setIsUpload] = useState<string>('file');
	const navigate = useNavigate();
	const { toast } = useToast();
	const [project, setProject] = useState<ProjectsModel | null>(null);
 
	const [ruleSet, setRuleSet] = useState<RulesetModel | null>(null);
	const [campaign, setCampaign] = useState<CampaignModel | null>(null);
	const [notes, setNotes] = useState<string | null>("");
	const [textToUpload, setTextToUpload] = useState<string>("");
	const [asset, setAsset] = useState<AssetModel | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isAnalyzing, setIsAnalyzing] = useState(false);
	const [review, setReview] = useState<string>("");
	const [analysisData, setAnalysisData] = useState<any>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [analysisId, setAnalysisId] = useState<string | null>(null);
	// const [isFile, setIsFile] = useState<string>('');
	const [politicalRiskAssmnt, setPoliticalRiskAssmnt] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [MultiRuleset,setMultiRuleset] = useState<RulesetExtItem[] | null>([]);
	const [url, setUrl] = useState<string | null>(null);
	const videoUrlRef = useRef("");
	const [videoUrlType, setVideoUrlType] = useState<"youtube" | "invalid" | null>(null);
	const [assetId, setAssetId] = useState<string | null>(null);
	const [fileType, setFileType] = useState<string | null>(null);
	const [fileData, setFileData] = useState<any | null>(null);
	
	const analysesRenderRef = useRef<{
		name?: string;
		project_id?: string;
		ruleset_id?: string;
		asset_id?: string;
		config?: { asset_description?: string };
		notes?: string;
		political_risk_ruleset_id?: string | null;
		lang?: string;
		locale_code?: string;
	}>({});
	
	const [isFormValid, setIsFormValid] = useState(false);
	const [isFormDirty, setIsFormDirty] = useState(false);

	const { id } = useParams(); // Extracts "id" from the URL
 
	const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
		let newText = event.target.value;
		newText = newText?.replace(/^\s+/, "");
		newText = newText?.replace(/\s+$/, " ");
		if (newText?.length <= CHARACTER_LIMIT) {
		  setTextToUpload(newText);
		}
		if (newText?.length >= 1){
		  window.isAnalysisDescFilled = true;
		} else{
		  window.isAnalysisDescFilled = false;
		}
	};

	useEffect(() => {
		return () => {
			window.isAnalysisDescFilled = false;
		};
	}, []);


	function clearData() {
		setTextToUpload("");
		setUrl("");
		setReview("");
		setError("");
		setFileType(null);
		setAsset(null);
		setFileData(null);
		window.isAnalysisDescFilled = false;
	}
 
	const cardStyles = css`
		display: flex;
		flex-direction: column;
		background-color: ${theme.colors.white};
		padding: ${theme.spacing["4xl"]};
		box-shadow: ${theme.shadows.medium};
		border-radius: ${theme.radius.xs};
		margin-top: ${theme.spacing["5xl"]};
		margin-bottom: ${theme.spacing.xxl};
	`;
 
	const cardLoaderStyles = css`
		display: none;
		flex-direction: column;
		padding: ${theme.spacing["4xl"]};
		border-radius: ${theme.radius.xs};
		margin-top: ${theme.spacing["5xl"]};
		margin-bottom: ${theme.spacing.xxl};
	`;
 
	const titleStyles = css`
		font-family: ${theme.typography.heading3_medium.fontFamily};
		font-size: ${theme.typography.heading3_medium.fontSize};
		font-weight: ${theme.typography.heading3_medium.fontWeight};
		color: ${theme.typography.heading3_medium.colors};
	`;
 
	const compulsoryStyles = css`
		font-family: ${theme.typography.subbody_medium.fontFamily};
		font-size: ${theme.typography.subbody_medium.fontSize};
		font-weight: ${theme.typography.subbody_medium.fontWeight};
		color: ${theme.typography.subbody_medium.colors};
	`;
 
	const radioItemStyles = {
		fontFamily: theme.typography.bodyMedium.fontFamily,
		fontSize: theme.typography.bodyMedium.fontSize,
		fontWeight: theme.typography.body.fontWeight,
		padding: "10px",
	} as React.CSSProperties;
 
	const formStyles = css`
		display: flex;
		flex-direction: column;
		margin-top: ${theme.spacing.xl};
		gap: ${theme.spacing.xl};
		font-family: ${theme.typography.bodyMedium.fontFamily};
		font-size: ${theme.typography.bodyMedium.fontSize};
		font-weight: ${theme.typography.bodyMedium.fontWeight};
		colors: ${theme.colors.purple};
	`;
 
	const inputRowStyles = css`
		display: flex;
		flex-wrap: wrap;
		gap: ${theme.spacing["4xl"]};
		> div {
			flex: 1;
			min-width: 200px;
		}
	`;
 
	const textareaStyles = css`
		color: ${theme.colors.textMedium};
		height: 172px;
		width: 100%;
		font-size: 16px;
		padding: 10px;
		box-sizing: border-box;
		resize: vertical;
		overflow: auto;
		border-radius: ${theme.radius.xs};
		border: 1px solid hsl(198, 11%, 71%);
 
		&:hover {
			background-color: var(--bg-secondary);
			border-color: var(--border-secondary);
		}
		&:focus {
			background-color: var(--bg-secondary);
			border-color: var(--border-brand-primary);
			outline: 2px solid transparent;
			outline-offset: 2px;
		}
	`;
 
	const counterStyles = css`
		font-size: ${theme.typography.bodyMedium.fontSize};
		color: ${textToUpload?.length >= CHARACTER_LIMIT
			? theme.colors.red
			: theme.colors.textMedium};
		text-align: right;
	`;
 
	const buttonContainerStyles = css`
		display: flex;
		justify-content: space-between;
		align-items: baseline;
		margin-top: ${theme.spacing.xl};

		@media (max-width: 768px) {
			flex-direction: column;
			align-items: flex-start;
			gap: 8px;

			> button {
				align-self: flex-end;
			}
    }
	`;
	const inputWrapper = css`
	  display: flex;
	  align-items: center;
	  border: 1px solid #ccc;
	  border-radius: 10px;
	  padding: 0px 0px;  
	  background: #fff;
	  margin: 5px 0px;
	  input {
	  border: none;
	  outline: none;
	  flex: 1;
	  font-size: 14px;
	   padding: 0.75rem 0.25rem;    
		border-radius: 0.5rem;
	  }
	 &:focus-within {
		border-color: ${theme.colors.primary};
		box-shadow: 0 0 0 2px ${theme.colors.primary};
		outline: 2px solid transparent;
		outline-offset: 2px;
		box-shadow: 0 0 0 2px #fff, 0 0 0 calc(2px + 2px) hsl(180, 98%, 25%), 0 0 #0000;
	  }
	  `;

const _triggerAssetDescription = () => {
	let _rulesetValues = MultiRuleset?.length
		? MultiRuleset.map((_item) => _item?.id).filter(Boolean).join(", ")
		: ruleSet?.id!;
	if (!(assetId || analysisData?.asset?.id) || !_rulesetValues || !language) return false;
	setIsLoading(true);
	setError("");
	triggerAssetDescription(
		assetId || analysisData?.asset?.id,
		_rulesetValues,
		language
	).then((response) => {
		if (response.success) {
			setAsset(response.asset);
			setTextToUpload(response.asset.description || response.asset?.config?.asset_description);
			setReview(response?.asset?.description);                      
		}
		setIsLoading(false);
	}).catch((err) => {
		toast({
			variant: "warning",
			title: t("form_assetErrorMsg"),
		});
		clearData();
	}).finally(() => {
		setIsLoading(false);
		setError("");
	});
}


useEffect(() => {
	if (window.isAnalysisDescFilled) {
		if (isUpload !== 'text') {
			_triggerAssetDescription();
		} else {
			setTextToUpload("");
		}
		window.isAnalysisDescFilled = false;
	}
	analysesRenderRef.current.lang = language;
	analysesRenderRef.current.locale_code = language;
}, [language]);

useEffect(() => {
	if (isUpload === 'url' && assetId) {
		_triggerAssetDescription();
	}
}, [assetId])

useEffect(()=>{
	let _rulesetValues = MultiRuleset?.length 
    ? MultiRuleset.map((_item) => _item?.id).filter(Boolean).join(', ') 
    : ruleSet?.id!;
	analysesRenderRef.current = {
		name: campaign?.name,
		project_id: project?.id!,
		ruleset_id: _rulesetValues,
		asset_id: isUpload === 'text' ? undefined : (asset?.id || analysisData?.asset?.id),
    	config: { asset_description:  textToUpload || review },
		political_risk_ruleset_id: politicalRiskAssmnt,
		notes: notes ?? "",
		lang: language,
		locale_code: language,
	};
	setIsFormValid(!!(
		(analysesRenderRef.current?.name!=null && analysesRenderRef.current?.name?.trim() !== '') &&
		analysesRenderRef.current?.project_id &&
		analysesRenderRef.current?.ruleset_id &&
		analysesRenderRef.current?.config?.asset_description?.trim() !== ''
	));

	if (analysisData &&
		(analysisData?.notes !== analysesRenderRef?.current?.notes ||
		analysisData?.config?.asset_description !== analysesRenderRef?.current?.config?.asset_description ||
		analysisData?.name !== analysesRenderRef.current?.name ||
		analysisData?.project?.id !== analysesRenderRef?.current?.project_id ||
		analysisData?.ruleset?.map((_item: any) => _item?.id)?.join(', ') !== analysesRenderRef?.current?.ruleset_id
	)) {
		setIsFormDirty(true);
	} else {
		setIsFormDirty(false);
	}
},[isUpload, campaign, ruleSet, review, notes, textToUpload, project, isFormDirty, politicalRiskAssmnt]);

const draftAnalysis = () => {
	if (isAnalyzing) return;
	setIsAnalyzing(true);

	let _rulesetValues = MultiRuleset?.length 
			? MultiRuleset.map((_item) => _item?.id).filter(Boolean).join(', ') 
			: ruleSet?.id!;
	const updatedAnalysis = {
		name: campaign?.name,
		project_id: project?.id,
		ruleset_id: _rulesetValues,
		asset_id: isUpload === 'text' ? undefined : (asset?.id || analysisData.asset?.id),
		config: {asset_description:
			textToUpload
			? textToUpload
			: review
			? review
			: analysisData?.config?.asset_description},
		political_risk_ruleset_id: politicalRiskAssmnt,
		notes: notes || "",
		aid: id,
		isUpload: isUpload !== 'text',
		lang: analysesRenderRef?.current?.lang,
		locale_code: analysesRenderRef?.current?.locale_code,
	};

	const newAnalysisId = analysisData?.id;
	patchAnalysis(newAnalysisId, updatedAnalysis).then(() => {
		setReRenderAnalysisList(true)
	}).finally(() => {
		setIsAnalyzing(false);
	});
  };
	useEffect(() => {
		if (id) {
			setAnalysisId(id);
		}
	}, [id]);
 
	useEffect(() => {
		let pollingInterval: NodeJS.Timeout | null = null;
		const fetchAnalysisData = async (id: string) => {
			setError(null);
 
			try {
				const data: any = await getAnalysis(id);
				setAnalysisData(data);
				setNotes(data?.notes || "");
				setAsset(data?.asset);
				if(data?.asset?.mime_type === "text/uri-list"){
					setVideoUrlType('youtube');
					setUrl(data?.asset?.original_url);
				}
				if(!data?.asset){
					setTextToUpload(data?.config?.asset_description || "");
				} else {
					setReview(data?.asset?.description || "");
				}
				if (data.status === "in_progress") {
					// Poll every 3 seconds
					pollingInterval = setTimeout(() => fetchAnalysisData(id), 10000);
				} else {        
					setTimeout(() => {
						setLoading(false);  
					}, 2500);
				 
				}
			} catch (err) {
				console.error(err);
				setError("Failed to fetch analysis data.");
				setLoading(false);
			}
		};
 
		if (analysisId) {
			fetchAnalysisData(analysisId);
		}
 
		return () => {
			if (pollingInterval) {
				clearTimeout(pollingInterval);
			}
		};
	}, [analysisId]);
 
	const launchAnalysis = () => {
		setIsFormDirty(false);
		if (isAnalyzing) return;
		setIsAnalyzing(true);

		let _rulesetValues = MultiRuleset?.length 
			? MultiRuleset.map((_item) => _item?.id).filter(Boolean).join(', ') 
			: ruleSet?.id!;
		const updatedAnalysis = {
			name: campaign!.name,
			project_id: project?.id,
			ruleset_id: _rulesetValues,
			asset_id: isUpload === 'text' ? undefined : (asset?.id || analysisData.asset?.id),
			config: {asset_description:
				textToUpload
				  ? textToUpload
				  : review
				  ? review
				  : analysisData?.config?.asset_description},
			notes: notes? notes: analysisData?.notes,
			political_risk_ruleset_id: politicalRiskAssmnt,
			aid: id,
			isUpload: isUpload !== 'text',
			lang: analysesRenderRef?.current?.lang,
			locale_code: analysesRenderRef?.current?.locale_code,
		};
		const newAnalysisId = analysisData?.id;
		patchAnalysis(newAnalysisId, updatedAnalysis).then((analysis) => {
			executeAnalysis(newAnalysisId, language).then(() => {
				setIsAnalyzing(false);
			}).catch((error) => {
				console.error(error);
			});
			navigate(`/analyses/results?analysis=${newAnalysisId}`);
		});
	}

	useEffect(()=>{
		if(analysisData){
			if (!analysisData?.asset?.mime_type) {
				setIsUpload('text');
			} else if (analysisData?.asset?.mime_type === "text/uri-list") {
				setIsUpload('url');
			} else {
				setIsUpload('file');
			}
			setFileData(analysisData?.asset);
		}
	},[analysisData]);

	const isValidYouTubeUrl = (url: string): boolean => {
		// Remove http:// or https:// at the start
		const cleanUrl = url.replace(/^https?:\/\//i, "").replace(/^www\./i, "").trim();
	  
		// Match YouTube watch, shorts, or youtu.be formats
		const pattern = /^(youtube\.com\/(watch\?v=|shorts\/)|youtu\.be\/)[\w-]{11}(?:[&?]\S*)?$/i;
	  
		return pattern.test(cleanUrl);
	  };

	function getVideoUrlType(url: string): "youtube" | "invalid" {

		if(!isValidYouTubeUrl(url)) return "invalid";
		  if (!url) return "invalid";
		  let parsedUrl: URL | null = null;
		  try {
			const normalizedUrl = url.match(/^https?:\/\//i) ? url : `https://${url}`;
			parsedUrl = new URL(normalizedUrl.toLowerCase());
			// ✅ Check for YouTube URLs
			  const isYouTube =
			  parsedUrl.hostname.includes("youtube.com") ||
			  parsedUrl.hostname.includes("youtu.be");
			  if (isYouTube) return "youtube";
			return "invalid";
		  } catch {
			return "invalid";
		  }
		}

	const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {   
		let value = e.target.value;
		videoUrlRef.current = value;
		setError("");
		const typeOfUrl = getVideoUrlType(value);
		setVideoUrlType(typeOfUrl);
		value = value.replace(/^(https?:\/\/)?(www\.)?/i, "");
		setUrl(value.trim());
		if (!value) {
		setError("Please enter a valid YouTube URL");
		} else {
		if (typeOfUrl === "invalid"){
			setError("Please enter a valid YouTube URL");
		} else if (typeOfUrl === "youtube") {      
			setIsLoading(true);
			setError("");
			createAsset({url: `https://www.${value}`, name: "youtube_link" }).then((res) => {
				setAssetId(res.id);
			})
		}
	};
	};
	
    if(isAnalyzing){
       window.scrollTo(0, 0)
       return <PageLoader />
    } 

	return (
		<>
		{ loading && <Loader/> }  
		<div css={loading? cardLoaderStyles: cardStyles}>        
		<div >
			{analysisData &&
			< >
				<div css={titleStyles} > {t("core_editAnalysis")} </div><form css={formStyles}>
					<div css={inputRowStyles}>
						<ProjectTextField setProject={setProject} projectId={analysisData?.project?.id} />
						<TextFieldWithRadioDropdown 
							setRulesets={setRuleSet} 
							setMultiRulesets={setMultiRuleset} 
							ruleId={analysisData.ruleset_id || analysisData?.ruleset?.map((val: any) => val?.id).join(',') || null} 
							setPoliticalRiskAssmnt={setPoliticalRiskAssmnt}
							politicalRiskId={analysisData?.political_risk_ruleset_id || null}
							fileType={fileType}
            				assetType={isUpload}
						/>
					</div>
					<div css={inputRowStyles}>
						<CampaignTextField setCampaign={setCampaign} campaignName={analysisData?.name} project={project?.id || analysisData?.project?.id || null} />
						<NotesField
							name="note"
							value={notes || ""}
							onChange={setNotes}
						/>
					</div>
					<RadioGroup
						name="uploadMethod"
						value={isUpload}
  						onValueChange={(v) => {clearData(); setIsUpload(v);}} 
						orientation="horizontal"
						className="radio-group"
						style={radioItemStyles}
					>
						<RadioGroupItem
							id="r1"
							label={t("form_inputFileImageDescription")}
							value="file"
							onClick={() => setIsUpload('file')}
						/>
						<RadioGroupItem
							id="r2"
							label={t("form_inputFileTexteDescription")}
							value="text"
							onClick={() => setIsUpload('text')}
						/>
						<RadioGroupItem
							id="r3"
							label={t("form_inputVideoUrlDescription")}
							value="url"
							onClick={() => setIsUpload('url')}
						/>
					</RadioGroup>
 
				 {isUpload==='file' && !fileData ? (
							<FileInput
										setAsset={setAsset}
										rulesetId={
											MultiRuleset?.length
												? MultiRuleset.map((item) => item?.id).filter(Boolean).join(", ")
												: ruleSet?.id ?? null
										}
										setIsLoading={setIsLoading}
										setReview={setReview}
										review={review}
										isLoading={isLoading}
										politicalRiskAssmnt={politicalRiskAssmnt}
									/>
				 ): null }
 
					{(isUpload==='file') && fileData ? (
						<FileInput
							setAsset={setAsset}
							assetId={fileData?.id}
							feedbackData={fileData?.feedbacks?.[0]}
							rulesetId={
								analysisData?.ruleset_id
								|| analysisData?.ruleset?.map((val: any) => val?.id).filter(Boolean).join(", ")
								|| null
							}
							setIsLoading={setIsLoading}
							setReview={setReview}
							review={review}
							isLoading={isLoading}
							politicalRiskAssmnt={politicalRiskAssmnt}
							fileData={{
								name: fileData?.title,
								type: fileData?.mime_type,
								url: fileData?.original_url,
							}} />
					) : null}
 
				{isUpload==='text' && (
					<div
							css={css`
							gap: 0;
						`}
						>
							<div css={counterStyles}>
								{textToUpload?.length}/{CHARACTER_LIMIT}
							</div>
							<textarea
								placeholder={t("form_inputTextareaPlaceholder")}
								css={textareaStyles}
								value={textToUpload}
								onChange={handleTextChange} />
						</div>
					)}

					{isUpload === 'url' && (
						<YoutubeUrlUpload
							isLoading={isLoading}
							url={url}
							setUrl={setUrl}
							textToUpload={textToUpload || review}
							videoUrlType={videoUrlType}
							error={error}
							clearData={clearData}
							handleUrlChange={handleUrlChange}
							handleTextChange={setTextToUpload}
							CHARACTER_LIMIT={CHARACTER_LIMIT}
							t={t}
							inputWrapper={inputWrapper}
							videoUrlRef={videoUrlRef}
							asset={asset}
						/>
						)
					}
 
					<div css={buttonContainerStyles}>
						<div css={compulsoryStyles}>{t("core_compulsory_field")}</div>
 
						<Button
							variant={"brand"}
							icon={"send"}
							color={theme.colors.primary}
							onClick={() => launchAnalysis()}
							disabled={!(isFormValid && !isLoading) || isAnalyzing}
							className="brandPrimaryBtn"
						>
							{t("form_inputButton")}
						</Button>
					</div>
				</form>
				</>
			}
			</div>
		</div>
		   <NavigationBlocker
				shouldBlock={isFormDirty}
				onClose={() => {}}
				dialogTitle={t("analysis_draft_title")}
				dialogMessage={t("do_you_want_save_changes")}
				onCancel={() => {}}
				cancelText={t("analysis_draft_cancel")}
				onProceed={
				  () => {
					draftAnalysis();
				  }
				}
				diableProceed={!(isFormValid && !isLoading) || isAnalyzing}
				proceedText={t("analysis_draft_save")}
				onDiscard={() => {}}
				descrdText={t("analysis_draft_no_delete")}
			  />
		</>
			);
 
}